import React, { useState, useEffect } from 'react';
import type { SaveList, SaveListVisibility } from '../../services/api';
import { savesApi } from '../../services/api';
import ListVisibilitySelector from '../common/ListVisibilitySelector';

interface AccessRightsModalProps {
  listId: string;
  list?: SaveList;
  onClose: () => void;
  onUpdate: (listId: string, visibility: SaveListVisibility, audienceUserIds: string[]) => void;
}

const AccessRightsModal: React.FC<AccessRightsModalProps> = ({
  listId,
  list,
  onClose,
  onUpdate
}) => {
  const [visibility, setVisibility] = useState<SaveListVisibility>('PRIVATE_SELF');
  const [audienceUserIds, setAudienceUserIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (list) {
      // Convert legacy format to new format if needed
      if (list.visibility) {
        setVisibility(list.visibility);
        setAudienceUserIds(list.audienceUserIds || []);
      } else {
        // Fallback to legacy
        const converted = savesApi.legacyToVisibility(list.isPublic || false, list.sharedWith || []);
        setVisibility(converted.visibility);
        setAudienceUserIds(converted.audienceUserIds);
      }
    }
  }, [list]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(listId, visibility, audienceUserIds);
    } finally {
      setSaving(false);
    }
  };

  if (!list) return null;

  // Render inline content without portal
  return (
    <div className="access-rights-view">
      <div className="access-rights-header">
        <div className="breadcrumb">
          Profile &gt; Saved &gt; {list.name} &gt; Privacy Settings
        </div>
      </div>
      <div className="access-rights-content">
        <h3 className="access-rights-title">List Privacy</h3>
        <p className="access-rights-description">
          Control who can view this list on your profile.
        </p>
        <ListVisibilitySelector
          visibility={visibility}
          audienceUserIds={audienceUserIds}
          onVisibilityChange={setVisibility}
          onAudienceChange={setAudienceUserIds}
        />
      </div>
      <div className="access-rights-footer">
        <button
          className="btn-secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default AccessRightsModal;